import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';
import { cleanupDuplicateNodes } from 'webapp/utils/html';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import type { NewItemOption } from 'webapp/components/common/new-item-options';
import CommonNewItemOptionsComponent from 'webapp/components/common/new-item-options';
import CommonRecentlyDeletedInfoComponent from 'webapp/components/common/recently-deleted-info';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { Args as ReportsIndexBaseArgs } from 'webapp/components/reports/index/base';
import ReportsIndexBaseComponent from 'webapp/components/reports/index/base';
import ReportsIndexGridItemComponent from 'webapp/components/reports/index/grid-item';
import SkeletonScreenComponent from 'webapp/components/skeleton-screen';
import TemplatesTransferedNotificationAccountGridItemComponent from 'webapp/components/templates/transfered-notification/account/grid-item';
import TemplatesTransferedNotificationPublicGridItemComponent from 'webapp/components/templates/transfered-notification/public/grid-item';

import FilterSortReportInGrid from 'webapp/models-custom/filter-sort/report-in-grid';
import type ReportsFirstPreviewTour from 'webapp/models-custom/tour/reports/first-preview';

export default class ReportsIndexGridComponent extends ReportsIndexBaseComponent {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('tour') declare tourService: Service.Tour;

	override filterSort: FilterSortReportInGrid;

	firstReportNode: HTMLElement | null = null;
	reportsFirstPreviewTour: ReportsFirstPreviewTour | null = null;

	newReportOptionTop: NewItemOption = {
		action: this.usePublicTemplate,
		icon: 'FolderOpen',
		text: 'Use template',
	};

	newReportOptionBottom: NewItemOption = {
		action: this.createReport,
		icon: 'PencilRuler',
		text: 'Start blank',
	};

	// Returning false skips the card's template / blank split.
	@action
	onNewReportCardClick(): boolean {
		if (!this.args.opensCreateReportModal) {
			return true;
		}

		this.args.onReportCreateChoice('reports');
		return false;
	}

	constructor(owner: Owner, args: ReportsIndexBaseArgs) {
		super(owner, args);

		const filterSort = new FilterSortReportInGrid({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.model;
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}

				this.args.updateIsRecentlyDeletedFilterActive(filterSort.isRecentlyDeletedSelected);
			},
		});

		this.filterSort = filterSort;

		this.eventManagerService.on(AppEvent.ALL_POPUPS_CLOSED, this, () => {
			later(() => {
				this.triggerReportsFavoriteTour();
			}, 1000);
		});
	}

	get hasFavoriteReports(): boolean {
		return this.model.filter((r) => r.favorite).length > 0;
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'li.filter-sort-item[data-id]',
			shouldKeepInSection: (itemId, sectionSelector) => {
				const report = this.model.find((r) => r.id === itemId);
				if (!report) {
					return false;
				}
				return (
					(report.favorite && sectionSelector === '.favorites-section') ||
					(!report.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}

	triggerReportsFavoriteTour(): void {
		const firstReportNode = this.firstReportNode;

		later(() => {
			if (
				this.tourService.isEligibleForReportsFavoriteTour &&
				firstReportNode &&
				!this.model.some((report) => report.favorite) &&
				!this.tourService.isEligibleForReportsFirstPreviewTour &&
				!this.popupService.anyPopupsOpen
			) {
				const tour = this.tourService.initTour(TourId.REPORTS__FAVORITE, {
					el: $(firstReportNode),
				});
				tour?.start();

				firstReportNode.classList.add('card--show-overlay');
				firstReportNode.querySelector('.favorite')?.addEventListener('click', () => {
					firstReportNode.classList.remove('card--show-overlay');
					if (tour) {
						tour.end();
					}
				});
			}
		}, 500);
	}

	@action
	initReportsFirstPreviewTour() {
		later(() => {
			const firstReportNode = this.firstReportNode;

			if (
				this.tourService.isEligibleForReportsFirstPreviewTour &&
				firstReportNode?.matches(':hover') &&
				!this.popupService.anyPopupsOpen
			) {
				const tour = this.tourService.initTour(TourId.REPORTS__FIRST_PREVIEW, {
					el: $(firstReportNode),
				});
				this.reportsFirstPreviewTour = tour;

				tour?.start();
			}
		}, 500);
	}

	@action
	hideReportsFirstPreviewTour() {
		if (this.reportsFirstPreviewTour) {
			this.reportsFirstPreviewTour.hideBubble();
		}
	}

	@action
	setupView(node: HTMLElement, sortingFavorites: boolean): void {
		this.visibleCheckContainerNode = node;

		const itemSelectorString = this.filterSort.itemDomSelector,
			placeholderElementFn = (/*currentItem*/) => {
				return $(
					'<div class="drop-placeholder flex-item"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></div>',
				);
			};
		this.initSortable($(node), itemSelectorString, placeholderElementFn, sortingFavorites);

		// only setup tours for the non-favorites section since that's what users see first
		if (!sortingFavorites) {
			const firstReportNode = node.querySelector('li.card--report');
			this.firstReportNode = firstReportNode as HTMLElement;

			firstReportNode?.addEventListener('mouseenter', this.initReportsFirstPreviewTour);
			firstReportNode?.addEventListener('mouseleave', this.hideReportsFirstPreviewTour);

			this.triggerReportsFavoriteTour();
		}
	}

	@action
	setupFavoritesView(node: HTMLElement): void {
		this.setupView(node, true);
	}

	@action
	setupNonFavoritesView(node: HTMLElement): void {
		this.setupView(node, false);
	}

	@action
	teardownView(): void {
		this.destroySortable();

		this.firstReportNode?.removeEventListener('mouseenter', this.initReportsFirstPreviewTour);
		this.firstReportNode?.removeEventListener('mouseleave', this.hideReportsFirstPreviewTour);
	}

	@action
	cleanupDuplicateNodes(): void {
		cleanupDuplicateNodes(this.duplicateCleanupConfig);
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.ALL_POPUPS_CLOSED, this);
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} @entityName="reports" />

		{{#if this.isRecentlyDeletedActiveWithResults}}
			<CommonRecentlyDeletedInfoComponent
				@onDeleteAllPermanently={{this.deleteAllPermanently}}
			/>
		{{/if}}

		{{#if this.isRecentlyDeletedLoading}}
			<SkeletonScreenComponent @type="tiles" @item="report" />
		{{/if}}

		{{! Notifications reports section }}
		{{#if this.isNotificationsSectionVisible}}
			{{#unless this.isRecentlyDeletedActive}}
				<ul class="flex-ct flex-ct--size-report notifications-section">
					{{#each @transferedReportAccountTemplateNotifications as |notification|}}
						<TemplatesTransferedNotificationAccountGridItemComponent
							@model={{notification}}
						/>
					{{/each}}

					{{#each @transferedReportPublicTemplateNotifications as |notification|}}
						<TemplatesTransferedNotificationPublicGridItemComponent
							@model={{notification}}
						/>
					{{/each}}

					{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
					{{#each (makeArray length=21)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}
				</ul>
			{{/unless}}
		{{/if}}

		{{! Favorites section }}
		{{#if this.hasFavoriteReports}}
			<CommonListLazyLoadComponent
				@classNames="lazy-grid"
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.favoriteReportsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>

				<ul
					class="flex-ct flex-ct--size-report favorites-section"
					data-hidden={{if this.noResults "true" "false"}}
					{{didInsert this.setupFavoritesView}}
					{{visibleCheckContainer}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{willDestroy this.teardownView}}
				>
					{{#each visibleItems as |item|}}
						<ReportsIndexGridItemComponent
							@model={{item}}
							@batchSelect={{this.batchSelect}}
							@hideSort={{exporter.hideSort}}
							@trackingOrigin={{this.trackingOrigin}}
						/>
					{{/each}}

					{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
					{{#each (makeArray length=exporter.itemsPerRow)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}

				</ul>
				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>
		{{/if}}

		{{! Non-favorites section }}
		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@filterExtraFn={{this.nonFavoriteReportsFilterFn}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{700}}
			@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
			as |visibleItems exporter|
		>

			<ul
				class="flex-ct flex-ct--size-report non-favorites-section"
				data-hidden={{if this.noResults "true" "false"}}
				{{didInsert this.setupNonFavoritesView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |item|}}
					<ReportsIndexGridItemComponent
						@model={{item}}
						@batchSelect={{this.batchSelect}}
						@hideSort={{exporter.hideSort}}
						@trackingOrigin={{this.trackingOrigin}}
						@onPermanentDelete={{this.handleSingleItemPermanentDelete}}
						@onRestore={{this.handleSingleItemRestore}}
					/>
				{{/each}}

				{{#if this.addBtnShown}}
					<CommonNewItemOptionsComponent
						@buttonText="New report"
						@top={{this.newReportOptionTop}}
						@bottom={{this.newReportOptionBottom}}
						@customOpenAction={{this.onNewReportCardClick}}
					/>
				{{/if}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}

			</ul>
			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}
		</CommonListLazyLoadComponent>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{fn @onReportCreateChoice "reports_new"}}
					@options={{this.addButtonOptions}}
					@onOptionClick={{this.doAddButtonAction}}
				/>
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
